/**
 * ConfirmDialog + ConfirmProvider/useConfirm.
 *
 * Used by: EVERY destructive or sensitive action in the three apps — ngừng bán
 * sản phẩm, thu hồi thiết bị, đổi quyền vai trò, hủy đơn, hoàn tiền, điều
 * chỉnh kho, từ chối / khôi phục bản dịch.
 *
 * THE REASON VARIANT IS THE POINT.
 *
 * `@sonauto/permissions` marks a set of permissions `requiresReason`, and the
 * API REJECTS those calls with `REASON_REQUIRED` unless an `x-action-reason`
 * header is present. So for those actions the dialog must collect a reason
 * BEFORE the request is made — the confirm button stays disabled until the
 * reason passes the same minimum the server enforces (`zReason`: trimmed,
 * >= 3 characters). Collecting it here rather than in each screen means the
 * audit trail can never end up with an empty justification.
 *
 * The resolved value is the reason string (or `''` when none was required), and
 * `null` when the user cancelled — so `if (reason === null) return;` is the
 * standard call site.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from './Button.js';
import { Modal } from './Modal.js';
import { Textarea } from './Input.js';
import { FormField } from './FormField.js';

/** Mirrors `zReason` in @sonauto/schemas: trim, min 3, max 500. */
export const MIN_REASON_LENGTH = 3;
export const MAX_REASON_LENGTH = 500;

export interface ConfirmOptions {
  readonly title: string;
  readonly message?: ReactNode;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  readonly tone?: 'danger' | 'primary';
  /**
   * Require a typed justification, sent by the caller as `x-action-reason`.
   * Set this for every permission the catalogue marks `requiresReason`.
   */
  readonly requireReason?: boolean;
  readonly reasonLabel?: string;
  readonly reasonPlaceholder?: string;
  /** Suggested reasons; clicking one fills the box. */
  readonly reasonSuggestions?: readonly string[];
}

export type ConfirmFn = (options: ConfirmOptions) => Promise<string | null>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface PendingState {
  readonly options: ConfirmOptions;
  readonly resolve: (value: string | null) => void;
}

export function ConfirmProvider({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const [pending, setPending] = useState<PendingState | null>(null);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const pendingRef = useRef<PendingState | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<string | null>((resolve) => {
      const state: PendingState = { options, resolve };
      pendingRef.current = state;
      setReason('');
      setTouched(false);
      setPending(state);
    });
  }, []);

  const settle = useCallback((value: string | null) => {
    const current = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    setReason('');
    setTouched(false);
    current?.resolve(value);
  }, []);

  const value = useMemo(() => confirm, [confirm]);
  const options = pending?.options;
  const needsReason = options?.requireReason === true;
  const trimmed = reason.trim();
  const reasonError =
    needsReason && touched && trimmed.length < MIN_REASON_LENGTH
      ? `Lý do phải có ít nhất ${MIN_REASON_LENGTH} ký tự.`
      : null;
  const canConfirm = !needsReason || trimmed.length >= MIN_REASON_LENGTH;

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {options !== undefined ? (
        <Modal
          open
          onClose={() => settle(null)}
          title={options.title}
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={() => settle(null)}>
                {options.cancelLabel ?? 'Hủy bỏ'}
              </Button>
              <Button
                variant={options.tone === 'primary' ? 'primary' : 'danger'}
                disabled={!canConfirm}
                onClick={() => {
                  setTouched(true);
                  if (!canConfirm) return;
                  settle(needsReason ? trimmed : '');
                }}
              >
                {options.confirmLabel ?? 'Xác nhận'}
              </Button>
            </>
          }
        >
          {options.message !== undefined ? (
            <div className="text-sm text-fg-muted">{options.message}</div>
          ) : null}

          {needsReason ? (
            <div className="mt-4">
              <FormField
                label={options.reasonLabel ?? 'Lý do thực hiện'}
                required
                error={reasonError}
                hint="Lý do được ghi vào nhật ký thao tác và không thể sửa về sau."
              >
                {(field) => (
                  <Textarea
                    {...field}
                    rows={3}
                    maxLength={MAX_REASON_LENGTH}
                    autoFocus
                    value={reason}
                    placeholder={options.reasonPlaceholder ?? 'Ví dụ: khách yêu cầu đổi món do chờ quá lâu'}
                    onChange={(e) => setReason(e.target.value)}
                    onBlur={() => setTouched(true)}
                  />
                )}
              </FormField>

              {options.reasonSuggestions !== undefined && options.reasonSuggestions.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {options.reasonSuggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setReason(s)}
                      className="rounded-full bg-surface-alt px-3 py-1 text-xs text-fg-muted ring-1 ring-inset ring-line hover:bg-surface-sunken"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </Modal>
      ) : null}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (ctx === null) throw new Error('useConfirm phải được dùng bên trong <ConfirmProvider>.');
  return ctx;
}

/**
 * Controlled variant, for the rare screen that owns its own open state (e.g. a
 * confirmation embedded in a wizard step). Most callers want `useConfirm`.
 */
export interface ConfirmDialogProps extends ConfirmOptions {
  readonly open: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: (reason: string) => void | Promise<unknown>;
}

export function ConfirmDialog({
  open,
  onCancel,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel,
  tone = 'danger',
  requireReason = false,
  reasonLabel,
  reasonPlaceholder,
}: ConfirmDialogProps): React.JSX.Element {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const trimmed = reason.trim();
  const canConfirm = !requireReason || trimmed.length >= MIN_REASON_LENGTH;

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            {cancelLabel ?? 'Hủy bỏ'}
          </Button>
          <Button
            variant={tone === 'primary' ? 'primary' : 'danger'}
            disabled={!canConfirm}
            onClick={() => {
              setTouched(true);
              if (!canConfirm) return;
              return onConfirm(requireReason ? trimmed : '');
            }}
          >
            {confirmLabel ?? 'Xác nhận'}
          </Button>
        </>
      }
    >
      {message !== undefined ? <div className="text-sm text-fg-muted">{message}</div> : null}
      {requireReason ? (
        <div className="mt-4">
          <FormField
            label={reasonLabel ?? 'Lý do thực hiện'}
            required
            error={touched && !canConfirm ? `Lý do phải có ít nhất ${MIN_REASON_LENGTH} ký tự.` : null}
          >
            {(field) => (
              <Textarea
                {...field}
                rows={3}
                maxLength={MAX_REASON_LENGTH}
                value={reason}
                placeholder={reasonPlaceholder}
                onChange={(e) => setReason(e.target.value)}
                onBlur={() => setTouched(true)}
              />
            )}
          </FormField>
        </div>
      ) : null}
    </Modal>
  );
}
